import { stripLegacyProviderTag } from './legacyImportedMessages.js';
// BL-177 Step 2d (section 6): THE alert copy for staff.inbound_message and every staff alert, on every channel.
// Push, bell, pop-up, email and text each get their own words, built from the payload FIELDS (contact, company,
// message, after hours...), never from the old combined `body` ("[legacy] hi — /communications?session=...").
//
// Pure and import-free on purpose: the browser (bell, pop-up) and the Deno edge function
// (notification-channel-dispatch) both import this exact file, so the words can't drift apart. Phone numbers are
// formatted by the caller's helper (src/lib/phone.js in the browser, supabase/functions/_shared/phoneFormat.ts in
// Deno), passed in as `formatPhone`.

export const PUSH_BODY_MAX = 120;
export const SMS_MAX = 160;
export const POPUP_DISMISS_MS = 8000;
export const POWERED_BY = 'Powered by smarTEKnowledgy';

const identity = (value) => (value == null ? '' : String(value));
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const THREAD_URL = /^\/communications\?session=([0-9a-f-]{36})$/i;

function str(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function looksLikePhone(value) {
    return /^\+?[\d\s().-]{5,}$/.test(str(value));
}

// ---------------------------------------------------------------------------------------------
// Text cleanup
// ---------------------------------------------------------------------------------------------

/** Cut to `max` characters, ending in "…" when cut. */
export function truncate(text, max, ellipsis = '…') {
    const value = String(text ?? '');
    if (value.length <= max) return value;
    return `${value.slice(0, Math.max(0, max - ellipsis.length)).trimEnd()}${ellipsis}`;
}

/**
 * The words a person should read: no "[legacy]"/"[Twilio]" provider tag, no URL or app path, no session id,
 * whitespace collapsed. Used on every message body and on any legacy title/body.
 */
export function cleanAlertText(text) {
    return stripLegacyProviderTag(String(text ?? ''))
        .replace(/^\s*\[(?:Twilio|SMS|MMS|WhatsApp)\]\s*/i, '')
        .replace(/\s*[—–-]\s*\/communications\?session=\S*/gi, '')
        .replace(/https?:\/\/\S+/gi, '')
        .replace(/\/communications\?\S*/gi, '')
        .replace(/\bsession=\S+/gi, '')
        .replace(UUID, '')
        .replace(/\s+/g, ' ')
        .replace(/\s+([,.!?;:])/g, '$1')
        .replace(/\s*[—–-]\s*$/, '')
        .trim();
}

// ---------------------------------------------------------------------------------------------
// GSM-7 (plain text-message characters)
// ---------------------------------------------------------------------------------------------

const GSM7_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXTENSION = '^{}\\[~]|€\f';

// Characters people type that a plain text message can't carry, and the plain version we send instead.
const GSM7_REPLACEMENTS = [
    [/[‘’‚‛′`´]/g, "'"],
    [/[“”„‟″«»]/g, '"'],
    [/[‐-―−]/g, '-'],
    [/…/g, '...'],
    [/[·•‧∙]/g, '-'],
    [/[  -   　\t]/g, ' '],
    [/[​-‍⁠﻿]/g, ''],
];

/** Septets this text costs in one GSM-7 message (extension characters cost 2). */
export function gsm7Length(text) {
    let total = 0;
    for (const char of String(text ?? '')) total += GSM7_EXTENSION.includes(char) ? 2 : 1;
    return total;
}

export function isGsm7(text) {
    for (const char of String(text ?? '')) {
        if (!GSM7_BASIC.includes(char) && !GSM7_EXTENSION.includes(char)) return false;
    }
    return true;
}

/** The same words in plain GSM-7: smart quotes, dashes, "…", "·" made plain; accents dropped; anything else removed. */
export function toGsm7(text) {
    let value = String(text ?? '');
    for (const [pattern, replacement] of GSM7_REPLACEMENTS) value = value.replace(pattern, replacement);
    let out = '';
    for (const char of value) {
        if (GSM7_BASIC.includes(char) || GSM7_EXTENSION.includes(char)) { out += char; continue; }
        const plain = char.normalize('NFD').replace(/[̀-ͯ]/g, '');
        if (plain && [...plain].every((c) => GSM7_BASIC.includes(c))) out += plain;
    }
    return out.replace(/ {2,}/g, ' ');
}

function gsm7Truncate(text, max, ellipsis = '...') {
    if (gsm7Length(text) <= max) return text;
    let out = '';
    for (const char of text) {
        if (gsm7Length(out + char) + ellipsis.length > max) break;
        out += char;
    }
    return `${out.trimEnd()}${ellipsis}`;
}

// ---------------------------------------------------------------------------------------------
// Company icon
// ---------------------------------------------------------------------------------------------

/** "The Ride" → "TR", "Ride ATL" → "RA", "Uber" → "UB". Never empty. */
export function monogram(name) {
    const words = String(name ?? '').replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
    if (!words.length) return 'IA';
    const letters = words.length === 1 ? [...words[0]].slice(0, 2).join('') : words.slice(0, 2).map((word) => [...word][0]).join('');
    return letters.toUpperCase();
}

/** A square monogram tile (navy, teal letters) as an SVG data URL, for when no brand icon is stored. */
export function monogramSvgDataUrl(name) {
    const letters = monogram(name).replace(/[<>&"']/g, '');
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="192" height="192" viewBox="0 0 192 192">'
        + '<rect width="192" height="192" rx="40" fill="#0B1220"/>'
        + '<rect x="6" y="6" width="180" height="180" rx="34" fill="none" stroke="#2DD4BF" stroke-width="6"/>'
        + `<text x="96" y="96" dy=".35em" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="76" font-weight="700" fill="#2DD4BF">${letters}</text>`
        + '</svg>';
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** The stored brand icon when it is a safe https image URL; otherwise null (use the monogram). */
export function brandIconUrl(value) {
    const url = str(value);
    return /^https:\/\/[^\s"'<>]+$/i.test(url) ? url : null;
}

function phoneDigits(value) {
    return String(value ?? '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
}

/**
 * The company an alert is about, from Brands & Locations: the business number's entity (org_twilio_numbers.entity_id),
 * walking up parent_entity_id because a blank brand name or logo inherits from the parent. Without a matching number:
 * the only Company, else the Organization. Returns everything a diagnosis needs:
 *   { companyName, brandIcon, entityId, logoEntityId, matchedBy }
 * entityId = the entity the alert resolved to; logoEntityId = the entity (it or a parent) whose logo is used (null: none);
 * matchedBy = 'number' | 'only_company' | 'organization' | 'none'. BL-177 Step 2f: the push records this, so a banner
 * with no logo can be traced to the exact reason afterwards.
 * @param {{entities?: Array<Record<string, any>>, numbers?: Array<Record<string, any>>, number?: string}} [input]
 */
export function resolveCompanyBrandDetail({ entities = [], numbers = [], number = '' } = {}) {
    const rows = Array.isArray(entities) ? entities : [];
    const byId = (id) => rows.find((row) => row && row.id === id);
    const match = number ? (Array.isArray(numbers) ? numbers : []).find((row) => row && phoneDigits(row.phone_number) === phoneDigits(number)) : null;
    const companies = rows.filter((row) => row && row.level_type === 'company');
    const fromNumber = match && match.entity_id ? byId(match.entity_id) : undefined;
    const onlyCompany = companies.length === 1 ? companies[0] : undefined;
    // A number with no company mapped, in an organization with several companies: if exactly one of them has a logo it is the
    // brand people know (the Organization row itself has none), so use it rather than falling back to a monogram.
    const logoCompanies = companies.filter((row) => str(row.logo));
    const onlyLogoCompany = logoCompanies.length === 1 ? logoCompanies[0] : undefined;
    const organization = rows.find((row) => row && row.level_type === 'organization');
    const start = fromNumber || onlyCompany || onlyLogoCompany || organization;
    const matchedBy = fromNumber ? 'number' : onlyCompany ? 'only_company' : onlyLogoCompany ? 'only_company_with_logo' : organization ? 'organization' : 'none';
    const inherited = (field) => {
        for (let row = start, hops = 0; row && hops < 10; row = byId(row.parent_entity_id), hops += 1) {
            const value = str(row[field]);
            if (value) return { value, entityId: row.id };
        }
        return { value: '', entityId: null };
    };
    const logo = inherited('logo');
    const brandName = inherited('brand_name');
    return {
        companyName: brandName.value || str(start && start.name),
        brandIcon: brandIconUrl(logo.value),
        entityId: start ? start.id : null,
        logoEntityId: logo.value ? logo.entityId : null,
        matchedBy,
    };
}

/** { companyName, brandIcon } for an alert (see resolveCompanyBrandDetail for the full answer). */
export function resolveCompanyBrand(input = {}) {
    const { companyName, brandIcon } = resolveCompanyBrandDetail(input);
    return { companyName, brandIcon };
}

// ---------------------------------------------------------------------------------------------
// Fields: one normalised view of an alert, whatever shape the payload arrived in
// ---------------------------------------------------------------------------------------------

export function sessionIdFromThreadUrl(url) {
    const match = THREAD_URL.exec(str(url));
    return match ? match[1].toLowerCase() : '';
}

/**
 * Normalise an alert payload (plus anything the caller looked up: company, session row, routing) into the fields
 * every builder uses. Accepts both the Step 2c trigger payload (contactName may be the raw number or
 * "a group text") and the Step 2d one (contactPhone, isGroup, afterHours, messageText, companyName...).
 */
export function alertFields(payload = {}, extra = {}, { formatPhone = identity } = {}) {
    const p = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
    const x = extra && typeof extra === 'object' ? extra : {};
    const rawName = str(p.contactName);
    const isGroup = p.isGroup === true || str(p.contactKind) === 'group' || x.isGroup === true || /^a group text$/i.test(rawName);
    const contactPhone = str(p.contactPhone) || str(x.contactPhone) || (looksLikePhone(rawName) ? rawName : '');
    const contactName = isGroup || looksLikePhone(rawName) || /^a group text$/i.test(rawName) ? '' : rawName;
    const threadUrl = THREAD_URL.test(str(p.threadUrl)) ? str(p.threadUrl)
        : (str(p.sessionId) && /^[0-9a-f-]{36}$/i.test(str(p.sessionId)) ? `/communications?session=${str(p.sessionId)}` : '');
    const message = cleanAlertText(str(p.messageText) || str(x.messageText) || str(p.message) || str(p.preview)
        || (p.body && /^\s*\[/.test(String(p.body)) ? p.body : ''));
    const companyName = str(x.companyName) || str(p.companyName) || str(p.company);
    return {
        eventType: str(x.eventType) || str(p.eventType),
        contactName,
        contactPhone,
        contactLabel: contactName || formatPhone(contactPhone) || 'Unknown sender',
        isGroup,
        groupSender: isGroup ? str(p.senderName) || str(p.sentByName) : '',
        afterHours: p.afterHours === true || p.after_hours === true || x.afterHours === true,
        companyName,
        businessNumber: str(p.number) || str(p.businessNumber) || str(x.businessNumber),
        message,
        sessionId: sessionIdFromThreadUrl(threadUrl) || str(p.sessionId),
        threadUrl,
        receivedAt: str(p.receivedAt) || str(x.receivedAt),
        timeZone: str(x.timeZone) || str(p.timeZone) || 'America/New_York',
        brandIcon: brandIconUrl(x.brandIcon) || brandIconUrl(p.brandIcon),
        severity: str(p.alertSeverity) || str(p.severity),
        title: cleanAlertText(p.title),
        body: cleanAlertText(p.body),
        flag: str(p.flag) || str(p.flagKey),
        channel: str(p.channel) || 'sms',
    };
}

// ---------------------------------------------------------------------------------------------
// Titles (shared by push, bell and pop-up)
// ---------------------------------------------------------------------------------------------

/** "Roger Dodger · The Ride" / "+49 163 5959331 · The Ride" / "Group text · The Ride" / "After hours · Roger Dodger". */
export function inboundAlertTitle(fields) {
    const company = fields.companyName;
    if (fields.afterHours) return `After hours · ${fields.isGroup ? 'Group text' : fields.contactLabel}`;
    const who = fields.isGroup ? 'Group text' : fields.contactLabel;
    return company ? `${who} · ${company}` : who;
}

function inboundMessageLine(fields) {
    const text = fields.message;
    return fields.isGroup && fields.groupSender && text ? `${fields.groupSender}: ${text}` : text;
}

export const NOTE_MENTION_EVENT = 'staff.note_mention';

/** The note a mention alert points at (a safe id), or ''. */
export function mentionNoteId(eventType, payload) {
    if (eventType !== NOTE_MENTION_EVENT) return '';
    const value = payload && typeof payload === 'object' ? str(payload.noteId) : '';
    return /^[A-Za-z0-9_-]{1,120}$/.test(value) ? value : '';
}

/** Title + one-line preview for any staff alert, as the bell and the pop-up show it. */
export function alertDisplay(eventType, payload, extra = {}, options = {}) {
    const fields = alertFields(payload, { ...extra, eventType }, options);
    if (eventType === 'staff.inbound_message') {
        return { title: inboundAlertTitle(fields), preview: inboundMessageLine(fields) || 'New text', fields };
    }
    // "Val mentioned you on Greta Johnson" / 'first ~80 characters of the note'. Only people who can see the conversation get it.
    if (eventType === NOTE_MENTION_EVENT) {
        return { title: fields.title || 'You were mentioned in a note', preview: fields.body, fields };
    }
    return { title: fields.title || 'Notification', preview: fields.body, fields };
}

// ---------------------------------------------------------------------------------------------
// Push
// ---------------------------------------------------------------------------------------------

/**
 * The Web Push / Expo payload for a staff alert: { title, body, url, tag, renotify, icon, monogram, data }.
 * body = the message only (<= 120, no provider tag, URL or session id); url = the conversation; tag = the
 * conversation so a newer text replaces the older one.
 */
/**
 * @param {{eventType?: string, payload?: object, extra?: object, notificationId?: string,
 *   formatPhone?: (value: unknown) => string}} [options]
 */
export function buildPushPayload({ eventType, payload, extra = {}, notificationId = '', formatPhone = identity } = {}) {
    const { title, preview, fields } = alertDisplay(eventType, payload, extra, { formatPhone });
    // Staff Messaging Phase 1: a note @mention opens its conversation at that note (&note=<id>), and never replaces a text alert.
    const noteId = mentionNoteId(eventType, payload);
    const url = fields.threadUrl ? `${fields.threadUrl}${noteId ? `&note=${noteId}` : ''}` : '/';
    const tag = noteId ? `mention:${fields.sessionId || 'note'}:${noteId}`
        : fields.sessionId ? `conversation:${fields.sessionId}` : `${eventType || 'alert'}:${notificationId || 'latest'}`;
    return {
        title: truncate(title, 80),
        body: truncate(preview, PUSH_BODY_MAX),
        url,
        threadUrl: url,
        tag,
        renotify: true,
        icon: fields.brandIcon || monogramSvgDataUrl(fields.companyName),
        monogram: monogram(fields.companyName),
        eventType,
        sessionId: fields.sessionId || null,
        notificationId: notificationId || null,
        afterHours: fields.afterHours,
    };
}

// ---------------------------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------------------------

/** "7:40 AM" in the company's time zone. */
export function formatAlertTime(iso, timeZone = 'America/New_York') {
    const date = iso ? new Date(iso) : new Date();
    if (Number.isNaN(date.getTime())) return '';
    try {
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone }).format(date);
    } catch {
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }).format(date);
    }
}

// ---------------------------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------------------------

function escapeHtml(value) {
    return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** The link when it is an absolute http(s) URL, otherwise '' (never a javascript: or data: link in an email). */
function safeHttpUrl(value) {
    const url = str(value);
    return /^https?:\/\/[^\s"'<>]+$/i.test(url) ? url : '';
}

/**
 * The email alert: { subject, text, html }. Sent from the company's own mailbox (white-label sender).
 *   Subject: New text from Roger Dodger
 *   Roger Dodger texted +1 (404) 594-2667 at 7:40 AM:
 *   "Can you pick me up at 6 instead of 5:30? Flight moved."
 *   [Open conversation]
 *   You get this because you handle texts for The Ride. Change my notifications · Powered by smarTEKnowledgy
 */
/**
 * @param {{payload?: object, extra?: object, openUrl?: string, settingsUrl?: string,
 *   formatPhone?: (value: unknown) => string}} [options]
 */
export function buildEmailAlert({ payload, extra = {}, openUrl, settingsUrl, formatPhone = identity } = {}) {
    const fields = alertFields(payload, extra, { formatPhone });
    const company = fields.companyName || 'your company';
    const who = fields.isGroup ? 'A group text' : fields.contactLabel;
    const subjectWho = fields.isGroup ? 'New group text' : `New text from ${fields.contactLabel}`;
    const subject = fields.afterHours ? `After hours · ${subjectWho}` : subjectWho;
    const number = formatPhone(fields.businessNumber);
    const time = formatAlertTime(fields.receivedAt || undefined, fields.timeZone);
    const intro = `${who} texted ${number || 'your business number'}${time ? ` at ${time}` : ''}:`;
    const quote = `"${inboundMessageLine(fields)}"`;
    const footer = `You get this because you handle texts for ${company}.`;
    const text = [
        fields.companyName || '',
        '',
        intro,
        '',
        quote,
        '',
        safeHttpUrl(openUrl) ? `Open conversation: ${safeHttpUrl(openUrl)}` : 'Open the conversation in IntelAgent.',
        '',
        `${footer} Change my notifications${safeHttpUrl(settingsUrl) ? `: ${safeHttpUrl(settingsUrl)}` : ''} · ${POWERED_BY}`,
    ].join('\n').replace(/^\n+/, '');
    // HTML version (board 02): inline styles only, table layout so Outlook keeps the width and the button, no remote fonts
    // (Exo 2 only when the reader already has it). Every piece of text that came from a person or the database is
    // escaped; links are used only when they are http(s). The company logo (Brands & Locations) shows when it is a safe
    // https image, with the company name as its alt text; nothing breaks when images are blocked.
    const font = "font-family:'Exo 2',Arial,Helvetica,sans-serif";
    const open = safeHttpUrl(openUrl);
    const settings = safeHttpUrl(settingsUrl);
    const logo = fields.brandIcon && fields.companyName
        ? `<img src="${escapeHtml(fields.brandIcon)}" alt="${escapeHtml(fields.companyName)}" width="40" height="40" style="display:block;width:40px;height:40px;border:0;border-radius:8px;margin:0 0 10px">`
        : '';
    const html = '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">'
        + '<meta name="viewport" content="width=device-width,initial-scale=1">'
        + `<title>${escapeHtml(subject)}</title></head>`
        + `<body style="margin:0;padding:0;background:#F1F5F9">`
        + `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all">${escapeHtml(`${intro} ${quote}`)}</div>`
        + '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#F1F5F9">'
        + '<tr><td align="center" style="padding:24px 12px">'
        + '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" '
        + `style="max-width:560px;background:#FFFFFF;border-radius:14px;${font};color:#0B1220">`
        + '<tr><td style="padding:24px 24px 20px">'
        + logo
        + (fields.companyName ? `<div style="margin:0 0 14px;font-size:20px;font-weight:800;line-height:1.3;color:#0B1220">${escapeHtml(fields.companyName)}</div>` : '')
        + (fields.afterHours ? '<div style="margin:0 0 12px"><span style="display:inline-block;border-radius:999px;padding:2px 10px;background:#6D28D9;color:#FFFFFF;font-size:12px;font-weight:700">After hours</span></div>' : '')
        + `<p style="margin:0 0 14px;font-size:15px;line-height:1.5"><b>${escapeHtml(who)}</b> texted ${escapeHtml(number || 'your business number')}${time ? ` at ${escapeHtml(time)}` : ''}:</p>`
        + `<div style="margin:0 0 20px;padding:14px;border-radius:12px;background:#F1F5F9;font-size:15px;line-height:1.5;white-space:pre-wrap">${escapeHtml(quote)}</div>`
        + (open ? '<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 22px"><tr>'
            + '<td bgcolor="#0B1220" style="border-radius:10px;background:#0B1220">'
            + `<a href="${escapeHtml(open)}" style="display:inline-block;padding:11px 18px;font-size:15px;font-weight:700;color:#FFFFFF;text-decoration:none;border-radius:10px;background:#0B1220;border-bottom:4px solid #050506">Open conversation</a>`
            + '</td></tr></table>' : '')
        + `<p style="margin:0;padding-top:12px;border-top:1px solid #E2E8F0;font-size:12px;line-height:1.5;color:#475569">${escapeHtml(footer)} `
        + (settings ? `<a href="${escapeHtml(settings)}" style="color:#0F766E">Change my notifications</a>` : 'Change my notifications')
        + ` · ${escapeHtml(POWERED_BY)}</p>`
        + '</td></tr></table></td></tr></table></body></html>';
    return { subject, text, html };
}

// ---------------------------------------------------------------------------------------------
// Staff text (SMS)
// ---------------------------------------------------------------------------------------------

/**
 * The staff text alert, plain GSM-7, <= 160:
 *   The Ride: New text from Roger Dodger: "Can you pick me up at 6 instead of 5:30? Flight moved. Also my..." https://app.therideatl.com/c/Ab3dE5gH7jK9
 * The link is the short link (/c/<code>, see conversation_short_links), so the preview gets all the room that is left
 * (at least ~60 characters with a normal company name); it shrinks to fit and the link is never cut.
 */
/**
 * @param {{payload?: object, extra?: object, link?: string, formatPhone?: (value: unknown) => string}} [options]
 */
export function buildSmsAlert({ payload, extra = {}, link = '', formatPhone = identity } = {}) {
    const fields = alertFields(payload, extra, { formatPhone });
    const company = toGsm7(fields.companyName);
    const who = toGsm7(fields.contactLabel);
    const what = fields.isGroup ? 'New group text' : `New text from ${who}`;
    const lead = `${company ? `${company}: ` : ''}${fields.afterHours ? 'After hours - ' : ''}${fields.afterHours ? what.charAt(0).toLowerCase() + what.slice(1) : what}`;
    const tail = link ? ` ${toGsm7(link)}` : '';
    // The preview sits inside our own quotes: drop wrapping quotes, make inner ones single.
    const message = toGsm7(inboundMessageLine(fields)).replace(/^["'\s]+/, '').replace(/"/g, "'");
    const room = SMS_MAX - gsm7Length(`${lead}: ""${tail}`);
    if (message && room >= 8) {
        const preview = gsm7Truncate(message, room);
        return `${lead}: "${preview}"${tail}`;
    }
    const short = `${lead}.${tail}`;
    if (gsm7Length(short) <= SMS_MAX) return short;
    // A very long company or contact name: cut the words, never the link.
    const leadRoom = SMS_MAX - gsm7Length(tail);
    return leadRoom >= 12 ? `${gsm7Truncate(lead, leadRoom)}${tail}` : gsm7Truncate(short, SMS_MAX);
}

/**
 * Any other staff alert text: its own title/body, cleaned, plain GSM-7, <= 160.
 * @param {{title?: string, body?: string, companyName?: string}} [options]
 */
export function buildGenericSmsAlert({ title, body, companyName = '' } = {}) {
    const parts = [companyName, cleanAlertText(title), cleanAlertText(body)].map(toGsm7).filter(Boolean);
    return gsm7Truncate(parts.join(': '), SMS_MAX);
}

// ---------------------------------------------------------------------------------------------
// Send path: does this channel go out to this person, right now?
// ---------------------------------------------------------------------------------------------

export const SKIP_COMPANY_OFF = 'skipped – turned off for this company';
export const SKIP_PERSONAL_OFF = 'Recipient notification preference disabled for this event/channel.';
export const SKIP_QUIET_HOURS = 'skipped – quiet hours';
export const SKIP_AFTER_HOURS_NOBODY = 'skipped – after hours, nobody until morning';
export const SKIP_AFTER_HOURS_OWNER_ONLY = 'skipped – after hours, owner only';
export const AFTER_HOURS_ROUTINGS = ['everyone', 'owner_only', 'nobody_until_morning'];

/** Push, text and voice interrupt a person; in-app and email simply arrive. */
export function isInterruptiveChannel(channel) {
    return ['push', 'sms', 'whatsapp', 'voice'].includes(String(channel || ''));
}

/**
 * The after-hours routing rule, identical to the database's after_hours_alert_allows(routing, role, channel, severity):
 * 'everyone' (default) → everyone; 'owner_only' → only the Owner; 'nobody_until_morning' → only the in-app bell entry
 * (no push, text or email). Critical alerts ignore routing.
 */
export function afterHoursAlertAllows(routing, roleLabel, channel, severity) {
    if (severity === 'critical') return true;
    const value = routing || 'everyone';
    if (value === 'owner_only') return roleLabel === 'Owner';
    if (value === 'nobody_until_morning') return channel === 'in_app';
    return true;
}

/**
 * The single send-path decision, after the database has answered its questions
 * (notification_company_enabled, notification_in_quiet_hours, notification_pref_enabled, after_hours_alert_allows):
 *   1. company switch off ⇒ nothing sends (critical-severity staff alerts keep their bypass);
 *   2. personal quiet hours ⇒ no push/text/voice (critical still goes; in-app and email still arrive);
 *   3. personal channel off ⇒ skip (a person can't turn ON what the company has off: the DB already folds that in);
 *   4. after hours ⇒ routing: 'everyone' (default) / 'owner_only' / 'nobody_until_morning' (bell only).
 * `afterHoursAllowed` is the database's answer when the caller has it; otherwise the same rule is applied here.
 * Returns { deliver, reason }.
 * @param {{eventType?: string, channel?: string, severity?: string, companyEnabled?: boolean, personalEnabled?: boolean,
 *   inQuietHours?: boolean, afterHours?: boolean, afterHoursRouting?: string, recipientRole?: string,
 *   afterHoursAllowed?: boolean}} [input]
 */
export function decideChannelDelivery({
    eventType = '', channel = '', severity = '', companyEnabled = true, personalEnabled = true,
    inQuietHours = false, afterHours = false, afterHoursRouting = 'everyone', recipientRole = '', afterHoursAllowed,
} = {}) {
    const critical = String(eventType).startsWith('staff.') && severity === 'critical';
    if (companyEnabled === false && !critical) return { deliver: false, reason: SKIP_COMPANY_OFF };
    if (inQuietHours === true && isInterruptiveChannel(channel) && !critical) return { deliver: false, reason: SKIP_QUIET_HOURS };
    if (personalEnabled !== true) return { deliver: false, reason: SKIP_PERSONAL_OFF };
    if (afterHours === true && !critical) {
        const routing = AFTER_HOURS_ROUTINGS.includes(afterHoursRouting) ? afterHoursRouting : 'everyone';
        const allowed = typeof afterHoursAllowed === 'boolean'
            ? afterHoursAllowed : afterHoursAlertAllows(routing, recipientRole, channel, severity);
        if (!allowed) return { deliver: false, reason: routing === 'owner_only' ? SKIP_AFTER_HOURS_OWNER_ONLY : SKIP_AFTER_HOURS_NOBODY };
    }
    return { deliver: true, reason: null };
}
